import { makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { MonthlySummary } from 'home/monthly_summary/types'
import { SummaryItem } from 'home/types'
import { TileItemDescription } from 'home/views/tiles/items/description'
import { Pressable, Text, View } from 'react-native'

interface MonthlySummaryItemContentProps {
  item: SummaryItem
  color?: string
  onPress?(): void
  error?: Error
}

type SummaryData = {
  hours: number
  earnings: number
  title: string
  delta?: number
}

export function MonthlySummaryItemContent({
  item,
  color,
  onPress,
  error,
}: MonthlySummaryItemContentProps) {
  const styles = useStyles(color)

  if (error) {
    return (
      <TileItemDescription
        description={item.description}
        error={error}
        textStyle={styles.errorText}
      />
    )
  }

  if (!item.params) {
    return null
  }

  const monthlySummary = item.params as MonthlySummary
  const actual = monthlySummary.actual
  const target = monthlySummary.target

  const summaryData: SummaryData[] = [
    {
      hours: target.hours,
      earnings: target.earnings,
      title: target.title ?? '',
    },
    {
      hours: actual.hours,
      earnings: actual.earnings,
      title: actual.title ?? '',
      delta: monthlySummary?.delta,
    },
  ]

  return (
    <Pressable style={styles.monthlySummaryContainer} onPress={onPress}>
      {summaryData.map((data, index) => (
        <MonthlySummaryRowView
          hours={data.hours}
          title={data.title}
          earnings={data.earnings}
          delta={data.delta}
          key={index}
          color={color}
        />
      ))}
    </Pressable>
  )
}
interface MonthlySummaryRowProps {
  hours: number
  earnings: number
  title: string
  delta?: number
  color?: string
}

function MonthlySummaryRowView({
  hours,
  earnings,
  title,
  delta,
  color,
}: MonthlySummaryRowProps) {
  const styles = useStyles(color)
  let emoji = ''

  if (delta) {
    emoji = delta > 0 ? '⬆️' : '⬇️'
  }

  return (
    <View style={styles.rowContainer}>
      <View style={styles.rowCellTitle}>
        <Text style={styles.rowTitleText}>{title}</Text>
      </View>

      <View style={styles.rowCell}>
        <Text style={styles.rowText}>{`${hours} ${t('hours')}`}</Text>
      </View>
      <View style={styles.rowCell}>
        <Text style={styles.rowText}>${Math.round(earnings)}</Text>
      </View>
      {delta ? (
        <View style={styles.deltaCell}>
          <Text style={styles.rowText}>
            {`${t('grossPay')} ${emoji} ${delta < 0 ? '-' : ''}$${Math.round(
              Math.abs(delta)
            )}`}
          </Text>
        </View>
      ) : (
        <View style={styles.deltaCell}></View>
      )}
    </View>
  )
}

const useStyles = makeStyles((theme, color: string | undefined) => ({
  monthlySummaryContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 2,
  },
  rowContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  rowCellTitle: {
    flex: 1.5,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  deltaCell: {
    flex: 2.5,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  rowCell: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  rowText: {
    fontSize: 10,
    color: color,
  },
  rowTitleText: {
    fontSize: 10,
    color: color,
    fontFamily: font.italic,
  },
  monthlySummaryTitle: {
    fontSize: 7,
    marginVertical: 3,
    color: color,
    fontFamily: font.bold,
  },
  errorText: {
    fontSize: 13,
    marginHorizontal: 16,
    marginVertical: 10,
    opacity: 0.8,
    color: color,
  },
}))
